import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { driver } from 'driver.js'
import { toast } from 'sonner'
import 'driver.js/dist/driver.css'
import '../styles/tour-theme.css'
import { tourStorage } from '../utils/tourStorage'
import { tourAnalytics } from '../utils/tourAnalytics'
import { getTourDefinition } from '../definitions'
import type { ReactNode } from 'react'
import type { Driver } from 'driver.js'

interface TourContextValue {
  startTour: (tourId: string) => void
  stopTour: () => void
  hasCompleted: (tourId: string) => boolean
  markCompleted: (tourId: string) => void
  resetTour: (tourId?: string) => void
}

const TourContext = createContext<TourContextValue | null>(null)

// Kill switch for every guided tour (set to true to disable)
export const TOURS_DISABLED = false

export function TourProvider({ children }: { children: ReactNode }) {
  const [driverInstance, setDriverInstance] = useState<Driver | null>(null)
  const currentTourIdRef = useRef<string | null>(null)

  useEffect(() => {
    const instance = driver({
      showProgress: true,
      progressText: '{{current}} of {{total}}',
      nextBtnText: 'Next →',
      prevBtnText: '← Back',
      doneBtnText: 'Got it!',
      showButtons: ['next', 'previous', 'close'],
      smoothScroll: true,
      animate: true,
      allowClose: true,
      stagePadding: 10,
      onPopoverRender: (popover) => {
        const state = instance.getState()
        // The step list lives on the config — the runtime state has no `steps`
        const stepCount = instance.getConfig().steps?.length ?? 0
        const activeIndex = state.activeIndex ?? 0

        if (activeIndex === 0) {
          popover.wrapper.classList.add('tour-first-step')
        }
        if (activeIndex === stepCount - 1) {
          popover.wrapper.classList.add('tour-last-step')
        }

        // Drives the progress rail in tour-theme.css
        popover.wrapper.style.setProperty(
          '--tour-progress',
          String(stepCount > 0 ? (activeIndex + 1) / stepCount : 1),
        )

        // driver.js reuses one popover element and resets its class list on
        // every step, so flush the removal before re-adding to replay the
        // enter animation instead of the card appearing to teleport.
        popover.wrapper.classList.remove('tour-step-enter')
        void popover.wrapper.offsetHeight
        popover.wrapper.classList.add('tour-step-enter')
      },
      onDestroyed: () => {
        if (currentTourIdRef.current) {
          const state = instance.getState()
          const config = instance.getConfig()
          const stepCount = config?.steps?.length ?? 0
          const currentStep = state?.activeIndex ?? 0
          const isCompleted = currentStep >= stepCount - 1

          if (isCompleted) {
            tourStorage.markCompleted(currentTourIdRef.current)
            tourAnalytics.trackTourCompleted(currentTourIdRef.current, 0)
          } else {
            tourStorage.incrementDismissCount(currentTourIdRef.current)
            tourAnalytics.trackTourDismissed(
              currentTourIdRef.current,
              currentStep,
              stepCount,
            )
          }

          currentTourIdRef.current = null
        }
      },
      onNextClick: () => {
        if (currentTourIdRef.current) {
          const state = instance.getState()
          tourAnalytics.trackTourStep(
            currentTourIdRef.current,
            state?.activeIndex ?? 0,
            'next',
          )
        }
        // Manually move to next step
        instance.moveNext()
      },
      onPrevClick: () => {
        if (currentTourIdRef.current) {
          const state = instance.getState()
          tourAnalytics.trackTourStep(
            currentTourIdRef.current,
            state?.activeIndex ?? 0,
            'previous',
          )
        }
        // Manually move to previous step
        instance.movePrevious()
      },
    })

    setDriverInstance(instance)

    return () => {
      instance.destroy()
    }
  }, [])

  const startTour = useCallback(
    (tourId: string) => {
      // Early return if tours are disabled
      if (TOURS_DISABLED) {
        console.log('[Tours] Tours are temporarily disabled')
        return
      }

      if (!driverInstance) {
        return
      }

      const tourDef = getTourDefinition(tourId)

      if (!tourDef) {
        console.error(`Tour definition not found: ${tourId}`)
        return
      }

      // Drop steps whose anchor isn't on the page — an empty list has no card
      // to point at, and driver.js silently turns those into centered
      // popovers, which reads as a mistake mid-tour. Steps without an element
      // are deliberate (the closing recap), so they always survive.
      const steps = tourDef.steps.filter(
        (step) =>
          typeof step.element !== 'string' ||
          document.querySelector(step.element) !== null,
      )

      // Nothing to point at at all — the tour was started from another page
      // (the tour menu is global). Say so instead of dead-ending silently.
      if (steps.length === 0) {
        toast.info('Open the page this tour covers, then start it again.')
        return
      }

      currentTourIdRef.current = tourId
      tourAnalytics.trackTourStarted(tourId)

      driverInstance.setSteps(steps)
      driverInstance.drive()
    },
    [driverInstance],
  )

  const stopTour = useCallback(() => {
    driverInstance?.destroy()
    currentTourIdRef.current = null
  }, [driverInstance])

  const hasCompleted = useCallback((tourId: string) => {
    return tourStorage.hasCompleted(tourId)
  }, [])

  const markCompleted = useCallback((tourId: string) => {
    tourStorage.markCompleted(tourId)
  }, [])

  const resetTour = useCallback((tourId?: string) => {
    tourStorage.reset(tourId)
  }, [])

  return (
    <TourContext.Provider
      value={{
        startTour,
        stopTour,
        hasCompleted,
        markCompleted,
        resetTour,
      }}
    >
      {children}
    </TourContext.Provider>
  )
}

export function useTour() {
  const context = useContext(TourContext)
  if (!context) {
    throw new Error('useTour must be used within TourProvider')
  }
  return context
}
